import { useNavigate } from 'react-router-dom'
import { DataTablePaging } from 'components/Shared/DataTablePaging'
import { type GridColDef, type GridEventListener } from '@mui/x-data-grid'
import { type LicensedEstablishmentUser } from 'types/api'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { useLayout } from 'hooks/useLayout'
import { formatDateTime } from 'utils/util'
import { DeleteLEConnectUserRenderCell } from './DeleteLEConnectUserRenderCell'
import { isNil } from 'lodash'
import { Box } from '@mui/material'
import { DeleteLEConnectUserModal } from './DeleteLEConnectUserModal'
import { useState } from 'react'

export const LEConnectUserTable = ({
  isPending,
  isError,
  data,
  totalCount,
  paginationModel,
  onPaginationModelChange,
}: {
  isPending: boolean
  isError: boolean
  data: LicensedEstablishmentUser[]
  totalCount: number
  paginationModel: any
  onPaginationModelChange: any
}) => {
  const navigate = useNavigate()
  const { isMobile } = useLayout()
  const [leUserRowData, setLEUserRowData] =
    useState<LicensedEstablishmentUser | null>()

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (params.field !== '__menu__' && params.id !== null) {
      navigate(`/ClientManagement/LEConnectUsers/${params.id}`)
    }
  }

  const defaultColumns = (isMobile: boolean): GridColDef[] => {
    return [
      {
        field: 'firstName',
        minWidth: 300,
        headerName: 'First Name',
        flex: 1,
        valueGetter: (params) => {
          return isMobile
            ? `${String(params.row.firstName)} ${String(params.row.lastName)}`
            : params.row.firstName
        },
      },
      {
        field: 'lastName',
        minWidth: 50,
        headerName: 'Last Name',
        flex: 1,
        valueGetter: (params) => {
          return isMobile ? '' : params.row.lastName
        },
      },
      {
        field: 'email',
        minWidth: 50,
        headerName: 'Email',
        flex: 1,
      },
      {
        field: 'lastAccessed',
        headerName: 'Last Accessed',
        minWidth: 130,
        flex: 1,
        valueGetter: (params) =>
          params.row.licensedEstablishmentUserMetrics.length >= 1
            ? params.row.licensedEstablishmentUserMetrics[
                params.row.licensedEstablishmentUserMetrics.length - 1
              ].lastAccessed
            : null,
        valueFormatter: ({ value }) => formatDateTime(value),
      },
      {
        field: '__menu__',
        headerName: '',
        align: 'left',
        renderCell: (params) => {
          return (
            <DeleteLEConnectUserRenderCell
              params={params}
              setLEUserRowData={setLEUserRowData}
            />
          )
        },
      },
    ]
  }

  if (isPending || isNil(data)) {
    return <ActivityIndicator />
  }

  if (isError) {
    return <p>Error loading users</p>
  }

  return (
    <Box paddingTop={{ xs: '20px', sm: '32px' }}>
      <DataTablePaging
        columns={defaultColumns(isMobile)}
        rows={data}
        totalCount={totalCount}
        getRowId={(row) => row.id ?? 0} // this should never happen, it's because our api defs indicate id is optional
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        noDataMessage="No J&J Connect Users Found"
        sortFieldName="firstName"
        onCellClick={selectCell}
        getRowClassName={() => 'text-[#2c2c27]'}
      />
      <DeleteLEConnectUserModal
        data={leUserRowData}
        isModalOpen={!isNil(leUserRowData)}
        toggleIsOpen={() => setLEUserRowData(undefined)}
      />
    </Box>
  )
}
